import {
  Actions as UnconfirmedDataActions,
  type UnconfirmedDataAction,
} from 'src/components/Profile/UnconfirmedData/actionCreators';
import type { Action } from 'src/types';

import { FileStatus, type Resume } from './types';

export enum Actions {
  RESUME_FILE_OPEN_ERROR = 'RESUME_FILE_OPEN_ERROR',
  RESUME_UPLOAD = 'RESUME_UPLOAD',

  RESUME_UPLOAD_START = 'RESUME_UPLOAD_START',
  RESUME_UPLOAD_SUCCESS = 'RESUME_UPLOAD_SUCCESS',
  RESUME_UPLOAD_FAILURE = 'RESUME_UPLOAD_FAILURE',

  RESUME_CREATE_START = 'RESUME_CREATE_START',
  RESUME_CREATE_SUCCESS = 'RESUME_CREATE_SUCCESS',
  RESUME_CREATE_FAILURE = 'RESUME_CREATE_FAILURE',

  RESUME_GET_LINK = 'RESUME_GET_LINK',
  RESUME_GET_LINK_START = 'RESUME_GET_LINK_START',
  RESUME_GET_LINK_SUCCESS = 'RESUME_GET_LINK_SUCCESS',
  RESUME_GET_LINK_FAILURE = 'RESUME_GET_LINK_FAILURE',

  RESUME_UPDATE = 'RESUME_UPDATE',
  RESUME_UPDATE_START = 'RESUME_UPDATE_START',
  RESUME_UPDATE_SUCCESS = 'RESUME_UPDATE_SUCCESS',
  RESUME_UPDATE_FAILURE = 'RESUME_UPDATE_FAILURE',

  RESUME_DELETE = 'RESUME_DELETE',
  RESUME_DELETE_START = 'RESUME_DELETE_START',
  RESUME_DELETE_SUCCESS = 'RESUME_DELETE_SUCCESS',
  RESUME_DELETE_FAILURE = 'RESUME_DELETE_FAILURE',

  RESUME_REQUEST_DELETE = 'RESUME_REQUEST_DELETE',
  RESUME_CANCEL_DELETE = 'RESUME_CANCEL_DELETE',
  RESUME_INVALID_FILE = 'RESUME_INVALID_FILE',

  RESUME_UPDATE_DEFAULT = 'RESUME_UPDATE_DEFAULT',
  RESUME_UPDATE_DEFAULT_START = 'RESUME_UPDATE_DEFAULT_START',
  RESUME_UPDATE_DEFAULT_SUCCESS = 'RESUME_UPDATE_DEFAULT_SUCCESS',
  RESUME_UPDATE_DEFAULT_FAILURE = 'RESUME_UPDATE_DEFAULT_FAILURE',

  RESUME_EDIT_LOADED = 'RESUME_EDIT_LOADED',

  RESUME_UPLOADED_MAKE_DEFAULT_CLICKED = 'RESUME_UPLOADED_MAKE_DEFAULT_CLICKED',
}

export enum ParsingState {
  UPLOADING = 'UPLOADING',
  PARSING = 'PARSING',
  COMPLETE = 'COMPLETE',
  STOPPED = 'STOPPED',
  FAILED = 'FAILED',
}

export interface DownloadRequestedResume {
  id: string;
  fetchedStatus: boolean;
}

interface State {
  resumeFileStatus: FileStatus;
  lastUploadedResume: string;
  deleteRequestedResume: string;
  downloadRequestedResumes: DownloadRequestedResume[];
  parsing: {
    pendingResumeId: string;
    pollAttempts: number;
    rolesFound: number;
    qualificationsFound: number;
    status: ParsingState;
  };
}

export const initialState: State = {
  resumeFileStatus: FileStatus.FILE_OK,
  lastUploadedResume: '',
  deleteRequestedResume: '',
  downloadRequestedResumes: [],
  parsing: {
    pendingResumeId: '',
    pollAttempts: 0,
    rolesFound: 0,
    qualificationsFound: 0,
    status: ParsingState.STOPPED,
  },
};

export type ResumeAction =
  | (Action<Actions.RESUME_UPLOAD_START> & {
      payload: {
        file: File;
        id: string;
        isDefault?: boolean;
      };
    })
  | (Action<Actions.RESUME_UPLOAD_SUCCESS | Actions.RESUME_CREATE_SUCCESS> & {
      payload: {
        newResumeId: string;
        isDefault?: boolean;
        showResumeParsing: boolean;
      };
    })
  | (Action<Actions.RESUME_UPLOAD_FAILURE | Actions.RESUME_CREATE_FAILURE> & {
      payload: {
        error: Error;
      };
    })
  | (Action<Actions.RESUME_UPLOADED_MAKE_DEFAULT_CLICKED> & {
      payload: {
        isChecked: boolean;
      };
    })
  | (Action<Actions.RESUME_REQUEST_DELETE> & { payload: string })
  | Action<Actions.RESUME_CANCEL_DELETE>
  | (Action<Actions.RESUME_GET_LINK_START> & { payload: string })
  | (Action<Actions.RESUME_GET_LINK_SUCCESS> & {
      payload: Resume;
    })
  | (Action<Actions.RESUME_GET_LINK_FAILURE> & {
      payload: { error: Error; id: string };
    })
  | (Action<Actions.RESUME_UPDATE_START> & { payload: string })
  | (Action<Actions.RESUME_UPDATE_SUCCESS> & {
      payload: Resume;
    })
  | (Action<Actions.RESUME_UPDATE_FAILURE> & {
      payload: { error: Error; id: string };
    })
  | (Action<Actions.RESUME_DELETE_START> & { payload: { id: string } })
  | Action<Actions.RESUME_DELETE_SUCCESS>
  | (Action<Actions.RESUME_DELETE_FAILURE> & {
      payload: { error: Error };
    })
  | Action<Actions.RESUME_UPDATE_DEFAULT_START>
  | Action<Actions.RESUME_UPDATE_DEFAULT_SUCCESS>
  | (Action<Actions.RESUME_UPDATE_DEFAULT_FAILURE> & {
      payload: {
        error: Error;
      };
    })
  | (Action<Actions.RESUME_INVALID_FILE> & { payload: FileStatus })
  | Action<Actions.RESUME_FILE_OPEN_ERROR>
  | Action<Actions.RESUME_EDIT_LOADED>
  | (Action<Actions.RESUME_CREATE_START> & {
      payload: { isDefault?: boolean };
    });

export const updateDownloadRequestedResumeList = (
  downloadRequestedResumeList: DownloadRequestedResume[],
  downloadRequestedResume: DownloadRequestedResume,
) => {
  const newDownloadRequestedResumeList = [...downloadRequestedResumeList];
  const index = newDownloadRequestedResumeList.findIndex(
    (res) => res.id === downloadRequestedResume.id,
  );
  if (index === -1) {
    newDownloadRequestedResumeList.unshift(downloadRequestedResume);
  } else {
    newDownloadRequestedResumeList[index] = downloadRequestedResume;
  }
  return newDownloadRequestedResumeList;
};

export const findResumeById = (id: string, resumeList: Resume[]): Resume =>
  resumeList.filter((resume: Resume) => resume.id === id)[0];

export const resumeReducer = (
  state: State = initialState,
  action: ResumeAction | UnconfirmedDataAction,
): State => {
  switch (action.type) {
    case Actions.RESUME_UPLOAD_START:
    case Actions.RESUME_CREATE_START: {
      return {
        ...state,
        parsing: {
          ...state.parsing,
          ...(action.type === Actions.RESUME_UPLOAD_START
            ? { status: ParsingState.UPLOADING }
            : {}),
        },
        resumeFileStatus:
          action.type === Actions.RESUME_CREATE_START
            ? FileStatus.FILE_EXPORTING
            : FileStatus.FILE_UPLOADING,
      };
    }

    case Actions.RESUME_UPLOAD_SUCCESS:
    case Actions.RESUME_CREATE_SUCCESS: {
      const { newResumeId, showResumeParsing } = action.payload;
      return {
        ...state,
        lastUploadedResume: newResumeId,
        parsing: {
          ...state.parsing,
          ...(action.type === Actions.RESUME_UPLOAD_SUCCESS &&
          !showResumeParsing
            ? { status: ParsingState.COMPLETE }
            : {}),
        },
        resumeFileStatus:
          action.type === Actions.RESUME_CREATE_SUCCESS
            ? FileStatus.FILE_EXPORT_OK
            : FileStatus.FILE_OK,
      };
    }

    case Actions.RESUME_UPLOAD_FAILURE:
    case Actions.RESUME_CREATE_FAILURE: {
      return {
        ...state,
        parsing: {
          ...initialState.parsing,
          status: ParsingState.FAILED,
        },
        resumeFileStatus:
          action.type === Actions.RESUME_CREATE_FAILURE
            ? FileStatus.FILE_EXPORT_ERROR
            : FileStatus.FILE_UPLOAD_ERROR,
      };
    }

    case Actions.RESUME_REQUEST_DELETE: {
      return {
        ...state,
        deleteRequestedResume: action.payload,
      };
    }

    case Actions.RESUME_UPDATE_START:
    case Actions.RESUME_GET_LINK_START: {
      return {
        ...state,
        downloadRequestedResumes: updateDownloadRequestedResumeList(
          state.downloadRequestedResumes,
          {
            id: action.payload,
            fetchedStatus: false,
          },
        ),
      };
    }

    case Actions.RESUME_UPDATE_SUCCESS:
    case Actions.RESUME_GET_LINK_SUCCESS: {
      return {
        ...state,
        downloadRequestedResumes: updateDownloadRequestedResumeList(
          state.downloadRequestedResumes,
          {
            id: action.payload.id,
            fetchedStatus: true,
          },
        ),
      };
    }

    case Actions.RESUME_CANCEL_DELETE: {
      return {
        ...state,
        deleteRequestedResume: '',
      };
    }

    case Actions.RESUME_DELETE_FAILURE: {
      return {
        ...state,
        deleteRequestedResume: '',
      };
    }

    case Actions.RESUME_INVALID_FILE: {
      return {
        ...state,
        resumeFileStatus: action.payload,
      };
    }

    case Actions.RESUME_FILE_OPEN_ERROR: {
      return {
        ...state,
        resumeFileStatus: FileStatus.FILE_OPEN_ERROR,
      };
    }

    case UnconfirmedDataActions.FETCH_UNCONFIRMED_DATA_START: {
      const { contextId: pendingResumeId } = action.payload;
      return {
        ...state,
        parsing: {
          ...initialState.parsing,
          status: ParsingState.PARSING,
          pendingResumeId,
        },
      };
    }

    case UnconfirmedDataActions.FETCH_UNCONFIRMED_DATA: {
      const { contextId: pendingResumeId } = action.payload;
      return {
        ...state,
        parsing: {
          ...state.parsing,
          pollAttempts: state.parsing.pollAttempts + 1,
          pendingResumeId,
        },
      };
    }

    case UnconfirmedDataActions.FETCH_UNCONFIRMED_DATA_SUCCESS: {
      const { roles, qualifications } = action.payload;

      return {
        ...state,
        parsing: {
          ...state.parsing,
          status: ParsingState.COMPLETE,
          rolesFound: roles.length,
          qualificationsFound: qualifications.length,
        },
      };
    }

    case UnconfirmedDataActions.FETCH_UNCONFIRMED_DATA_FAILURE:
    case UnconfirmedDataActions.FETCH_UNCONFIRMED_DATA_TIMEOUT: {
      return {
        ...state,
        parsing: {
          ...initialState.parsing,
          status: ParsingState.FAILED,
        },
      };
    }

    default:
      return state;
  }
};
